// Reusable Agora video UI: broadcast controls (host), audience watch view,
// and the private 1-to-1 meeting room. Styles are scoped via <style> so
// nothing else in the app needs to know.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Camera, CameraOff, Mic, MicOff, PhoneOff, Radio, Video, X } from "lucide-react";
import {
  humanizePermissionError, isConfigured,
  joinBroadcast, joinMeeting, meetingChannel,
} from "@/agora";
import { useAuth } from "@/auth";
import {api} from "@/api";
import {useEntitlements, UpgradePlan} from './Entitlements';
import {LiveInteractions, LiveHostInteractions} from '@/LiveInteractions';

const CFG_MISSING = "Video service isn't configured. Please contact support.";

// ---------- Small helpers ----------
function TrackVideo({ track }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!track || !ref.current) return;
    try { track.play(ref.current); } catch {}
    return () => { try { track.stop(); } catch {} };
  }, [track]);
  return <div ref={ref} className="video-surface"/>;
}

const PUBLIC_LABELS = { offline: "OFFLINE", connecting: "CONNECTING", live: "LIVE", reconnecting: "RECONNECTING", ending: "ENDING", ended: "ENDED", unavailable: "UNAVAILABLE", error: "ERROR" };

export function liveWindowOpen(show, now = Date.now()) {
  const serverNow = show?.server_time != null ? show.server_time * 1000 + now - (show.live_received_at || now) : now;
  return !!show?.live_allowed && !!show?.closes_at && serverNow < show.closes_at * 1000;
}

export function ExhibitionLiveStatus({exhibition}) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const timer = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(timer); }, []);
  const e = exhibition || {};
  const serverNow = e.server_time != null ? e.server_time * 1000 + now - (e.live_received_at || now) : now;
  const next = e.next_open_at ? new Date(e.next_open_at * 1000).toLocaleString(undefined, {timeZoneName:'short'}) : null;
  const remaining = e.next_open_at ? Math.max(0, Math.ceil((e.next_open_at * 1000 - serverNow) / 60000)) : null;
  let label = 'PUBLIC LIVE UNAVAILABLE';
  if (liveWindowOpen(e, now)) label = 'LIVE HOURS OPEN';
  else if (e.live_schedule_state === 'EXHIBITION_COMPLETED') label = 'EXHIBITION COMPLETED';
  else if (e.live_schedule_state === 'CLOSED_FOR_TODAY' || e.live_schedule_state === 'LIVE_HOURS_OPEN') label = 'CLOSED FOR TODAY';
  else if (['UPCOMING','OPENS_IN'].includes(e.live_schedule_state)) label = remaining > 0 ? `OPENS IN ${Math.floor(remaining / 60)}h ${remaining % 60}m` : 'Checking LIVE hours…';
  return <span data-testid={`live-schedule-${e.id || 'none'}`} role="status">{e.live_schedule_state === 'UPCOMING' && 'UPCOMING · '}{label}{next && ` · NEXT LIVE: ${next}`}{e.live_timezone && ` · Exhibition timezone: ${e.live_timezone}`}</span>;
}

// ---------- 1. Exhibitor broadcast controls ----------
export function BroadcastControls({ exhibitor, exhibitions = [], onLiveChange }) {
  const policy = useEntitlements();
  const [extraCamera, setExtraCamera] = useState(false);
  const [selectedShow, setSelectedShow] = useState('');
  const [clock, setClock] = useState(Date.now());
  useEffect(() => { const timer = setInterval(() => setClock(Date.now()), 1000); return () => clearInterval(timer); }, []);
  const openShows = exhibitions.filter(e => liveWindowOpen(e, clock));
  const chosen = exhibitions.find(e => e.id === selectedShow) || (openShows.length === 1 ? openShows[0] : null);
  const hasLiveExhibition = liveWindowOpen(chosen, clock);
  const [session, setSession] = useState(null);
  const [status, setStatus] = useState("offline");
  const [error, setError] = useState("");
  const current = useRef(null), operation = useRef(null), mounted = useRef(false), busy = useRef(false);
  const cameraMode = useRef(false);
  const notify = useRef(onLiveChange); notify.current = onLiveChange;
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false; operation.current?.abort();
      current.current?.leave().catch(() => {}); current.current = null;
      if (!cameraMode.current) notify.current?.(false);
    };
  }, [exhibitor.id]);
  const start = async (additionalCamera = false) => {
    if (busy.current || current.current || !hasLiveExhibition) return;
    if (!isConfigured()) { setError(CFG_MISSING); setStatus("error"); return; }
    busy.current = true; setError(""); setStatus("connecting");
    cameraMode.current = additionalCamera;
    const controller = new AbortController(); operation.current = controller;
    try {
      const next = await joinBroadcast({exhibitorId: exhibitor.id, exhibitionId: chosen.id, asHost: true, additionalCamera, signal: controller.signal,
        onState: value => {
          if (!mounted.current || controller.signal.aborted) return;
          setStatus(value); if (!cameraMode.current) notify.current?.(value === "live");
          if (["ended", "unavailable", "error"].includes(value)) { current.current = null; setSession(null); }
        },
        onError: message => { if (mounted.current && !controller.signal.aborted) setError(message); },
      });
      if (!mounted.current || controller.signal.aborted) { await next.leave(); return; }
      current.current = next; setSession(next); setExtraCamera(additionalCamera);
    } catch (err) {
      if (mounted.current && !controller.signal.aborted) {
        setError(humanizePermissionError(err)); setStatus(err.status === 409 ? "unavailable" : "error"); if (!cameraMode.current) notify.current?.(false);
      }
    } finally { busy.current = false; }
  };
  const stop = async () => {
    if (busy.current) return;
    busy.current = true; setStatus("ending");
    try { await current.current?.leave(); }
    catch (err) { if (mounted.current) setError(humanizePermissionError(err)); }
    finally {
      operation.current?.abort(); current.current = null; busy.current = false;
      if (mounted.current) { setSession(null); setStatus("offline"); if (!cameraMode.current) notify.current?.(false); }
    }
  };
  return <div className="broadcast-panel" data-testid="broadcast-panel">
    <style>{BROADCAST_STYLES}</style>
    <div className="broadcast-panel-top">
      <div><div className="eyebrow">PUBLIC LIVE BOOTH</div>
        <h2 className="broadcast-title">{status === "live" ? "You're live." : hasLiveExhibition ? "Ready when you are." : "Broadcasting is closed."}</h2>
        <p className="broadcast-copy" role="status" data-testid="broadcast-state">{PUBLIC_LABELS[status]}</p>
        <div>{exhibitions.map(show => <p key={show.id}><strong>{show.short || show.name}: </strong><ExhibitionLiveStatus exhibition={show}/></p>)}</div>
        {exhibitions.length > 1 && <label>Exhibition <select data-testid="broadcast-exhibition-select" value={selectedShow} onChange={e => setSelectedShow(e.target.value)} disabled={!!session}>
          <option value="">{openShows.length === 1 ? `Automatic: ${openShows[0].short || openShows[0].name}` : 'Choose exhibition'}</option>
          {exhibitions.map(e => <option key={e.id} value={e.id}>{e.short || e.name}</option>)}
        </select></label>}
        {!hasLiveExhibition && <p>Public LIVE is closed or an exhibition must be selected. Your catalogue and private meetings remain available.</p>}
      </div>
      <div className="broadcast-actions">
        {session ? <button className="button button-dark button-danger" onClick={stop} disabled={status === "ending"} data-testid="broadcast-end-button"><PhoneOff size={14}/>{status === "ending" ? "Ending…" : extraCamera ? "Stop this camera" : "End live"}</button>
          : <button className="button button-dark" onClick={()=>start(false)} disabled={!hasLiveExhibition || ["connecting", "ending"].includes(status)} data-testid="broadcast-start-button"><Radio size={15}/>{status === "connecting" ? "Starting…" : "Go live"}</button>}
        {!session&&policy&&policy.public_cameras>1&&<button className="button" onClick={()=>start(true)} disabled={!hasLiveExhibition||status==='connecting'} data-testid="broadcast-add-camera">Join as another camera</button>}
        {policy&&policy.public_cameras<=1&&<p>Add another LIVE camera 🔒 <UpgradePlan>Upgrade to Professional</UpgradePlan></p>}
      </div>
    </div>
    {policy&&policy.public_cameras>1&&<p>Up to {policy.public_cameras} cameras. Start the main broadcast first; on another device, sign in to this account and choose “Join as another camera”. Ending the main broadcast ends all cameras.</p>}
    {session?.sessionId && <HostViewerCount exhibitorId={exhibitor.id} sessionId={session.sessionId}/>}
    {session && ["live", "reconnecting"].includes(status) && <div className="broadcast-preview" data-testid="broadcast-preview">
      <span className="live-tag">{PUBLIC_LABELS[status]}</span><TrackVideo track={session.localTracks?.[1]}/>
    </div>}
    {error && <div className="broadcast-error" role="alert" data-testid="broadcast-error">{error}</div>}
    <LiveHostInteractions exhibitorId={exhibitor.id} sessionId={session?.sessionId}/>
  </div>;
}

// ---------- 2. Buyer watch-live: never shares clients across accounts ----------
function HostViewerCount({exhibitorId, sessionId}) {
  const [counts,setCounts] = useState(null);
  useEffect(() => {
    let alive=true,timer;
    const poll=async()=>{try{const data=await api.communication(`/live-presence/${exhibitorId}/${sessionId}`);if(alive)setCounts(data);}catch{}finally{if(alive)timer=setTimeout(poll,15000);}};
    poll();return()=>{alive=false;clearTimeout(timer);};
  },[exhibitorId,sessionId]);
  return counts ? <p data-testid="host-viewer-count">{counts.current_viewers} watching · {counts.verified_viewers} verified · {counts.unique_accounts} unique accounts</p> : null;
}

export function WatchLiveModal({ exhibitor, onClose }) {
  const {user} = useAuth();
  const identity = `${user?.user_id}:${user?.role}:${user?.exhibitor_id}`;
  return <PublicViewer key={`${identity}:${exhibitor.id}`} exhibitor={exhibitor} onClose={onClose}/>;
}

function PublicViewer({ exhibitor, onClose }) {
  const shell = useRef(null);
  const [expanded, setExpanded] = useState(false), [fullscreen, setFullscreen] = useState(false), [viewers, setViewers] = useState(null);
  const [session, setSession] = useState(null), [remoteUsers, setRemoteUsers] = useState([]);
  const [status, setStatus] = useState("connecting"), [error, setError] = useState(""), [attempt, setAttempt] = useState(0);
  useEffect(() => {
    const node = shell.current;
    const changed = () => setFullscreen(document.fullscreenElement === node);
    const escape = e => {if(e.key === "Escape") setExpanded(false);};
    document.addEventListener("fullscreenchange", changed); document.addEventListener("keydown", escape);
    return () => {document.removeEventListener("fullscreenchange", changed);document.removeEventListener("keydown", escape);if(document.fullscreenElement === node) document.exitFullscreen?.().catch(()=>{});};
  }, []);
  useEffect(() => {
    if(!session?.sessionId) return;
    let alive=true,timer;
    const poll=async()=>{try{const data=await api.communication(`/live-presence/${exhibitor.id}/${session.sessionId}`);if(alive)setViewers(data);}catch{}finally{if(alive)timer=setTimeout(poll,15000);}};
    poll();return()=>{alive=false;clearTimeout(timer);};
  },[session,exhibitor.id]);
  const toggleFullscreen = async () => {
    if(document.fullscreenElement === shell.current) {try{await document.exitFullscreen();}catch{}return;}
    if(expanded){setExpanded(false);return;}
    try {if(!shell.current.requestFullscreen) throw new Error("unavailable"); await shell.current.requestFullscreen();}
    catch {setExpanded(true);}
  };
  useEffect(() => {
    const controller = new AbortController(); let current;
    setStatus("connecting"); setError(""); setRemoteUsers([]); setSession(null);
    joinBroadcast({exhibitorId: exhibitor.id, asHost: false, signal: controller.signal,
      onState: value => { if (!controller.signal.aborted) setStatus(value); },
      onUsers: users => { if (!controller.signal.aborted) setRemoteUsers(users); },
      onError: message => { if (!controller.signal.aborted) setError(message); },
    }).then(s => { current = s; if (controller.signal.aborted) s.leave().catch(() => {}); else setSession(s); })
      .catch(err => { if (!controller.signal.aborted) { setStatus([403,404,409].includes(err.status) ? "unavailable" : "error"); setError(humanizePermissionError(err)); } });
    return () => { controller.abort(); current?.leave().catch(() => {}); };
  }, [exhibitor.id, attempt]);
  const hosts = remoteUsers.filter(u => u.videoTrack);
  const host = hosts[0];
  const terminal = ["error", "ended", "unavailable"].includes(status);
  return <div className={`video-modal public-viewer ${expanded ? "expanded" : ""}`} data-testid="watch-live-modal">
    <style>{MODAL_STYLES}{`.public-viewer .video-modal-shell{width:min(1280px,100%);max-height:94dvh}.public-viewer .video-modal-body{overflow:auto}.public-viewer .video-stage{flex-shrink:0;max-height:72dvh;width:min(100%,128dvh);align-self:center;object-fit:contain}.public-viewer.expanded{padding:0}.public-viewer.expanded .video-modal-shell{width:100%;height:100dvh;max-height:100dvh}.public-viewer .video-modal-shell:fullscreen{width:100vw;height:100dvh;max-height:none}.public-viewer .video-stage video{object-fit:contain!important}@media(max-width:600px){.public-viewer{padding:8px}.public-viewer .video-modal-shell header{padding:10px;flex-wrap:wrap}.public-viewer .video-modal-body{padding:8px}.public-viewer .video-modal-actions{flex-wrap:wrap}}`}</style><div className="video-modal-shell" ref={shell}>
      <header><div><div className="eyebrow light">PUBLIC BOOTH</div><h3>{exhibitor.name}</h3></div>
        <div className="video-modal-actions"><span className="live-tag" role="status" data-testid="watch-live-state">{PUBLIC_LABELS[status]}</span>
          <button className="button" onClick={toggleFullscreen} data-testid="live-fullscreen">{fullscreen || expanded ? "Exit fullscreen" : "Fullscreen"}</button>
          <button className="video-close-btn" onClick={onClose} aria-label="Close" data-testid="watch-live-close"><X size={16}/></button>
        </div>
      </header>
      <div className="video-modal-body live-booth-layout">
        <div className="live-booth-media">
        {viewers && !terminal && <small data-testid="live-viewers">{viewers.current_viewers} watching · {viewers.verified_viewers} verified · {viewers.unique_accounts} unique accounts</small>}
        {error && <div className="video-error" data-testid="watch-live-error">{error}</div>}
        {!terminal && status === "connecting" && <div className="video-loading">Connecting to the live booth…</div>}
        {!terminal && status === "reconnecting" && <div className="video-loading" data-testid="watch-live-waiting">Reconnecting to the broadcaster…</div>}
        {!terminal && host && <div className="camera-grid" style={hosts.length===1?{gridTemplateColumns:'1fr'}:undefined}>{hosts.map(camera=><div key={camera.uid} className="video-stage"><TrackVideo track={camera.videoTrack}/></div>)}</div>}
        {!terminal && status === "live" && !host && <div className="video-loading">LIVE … Audio only</div>}
        {session && !terminal && <button className="button" onClick={() => session.playAudio()} data-testid="watch-live-audio">Play audio</button>}
        {terminal && <button className="button" onClick={() => setAttempt(n => n + 1)} data-testid="watch-live-retry">Try again</button>}
        </div>
        {session?.sessionId && <LiveInteractions exhibitorId={exhibitor.id} sessionId={session.sessionId} ended={terminal}/>}
      </div>
    </div>
  </div>;
}

// ---------- 3. Private meeting room ----------
export function MeetingRoomModal(props) {
  const {user} = useAuth();
  if (!user) return null;
  return <PrivateMeeting key={`${user?.user_id}:${user?.role}:${user?.exhibitor_id}:${props.meeting.id}:${props.meeting.room_version}`} {...props}/>;
}

function PrivateMeeting({ meeting, exhibitorName, onClose }) {
  const [session, setSession] = useState(null);
  const [remoteUsers, setRemoteUsers] = useState([]);
  const [error, setError] = useState("");
  const [connecting, setConnecting] = useState(true);
  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(true);
  const [connState, setConnState] = useState("connecting");

  useEffect(() => {
    const controller = new AbortController(); let current;
    setSession(null); setRemoteUsers([]); setError(""); setConnecting(true);
    joinMeeting({channel: meetingChannel(meeting.id), signal: controller.signal,
      onUsers: users => { if (!controller.signal.aborted) setRemoteUsers(users); },
      onState: value => { if (!controller.signal.aborted) setConnState(value); },
      onError: message => { if (!controller.signal.aborted) { setError(message); setRemoteUsers([]); setSession(null); } },
    }).then(s => { current = s; if (controller.signal.aborted) s.leave(); else setSession(s); })
      .catch(e => { if (!controller.signal.aborted) { setError(humanizePermissionError(e)); setConnState("failed"); } })
      .finally(() => { if (!controller.signal.aborted) setConnecting(false); });
    return () => { controller.abort(); current?.leave().catch(() => {}); };
  }, [meeting.id]);

  const toggleMic = async () => {
    if (!session?.localTracks?.[0]) return;
    const next = !micOn;
    await session.localTracks[0].setEnabled(next);
    setMicOn(next);
  };
  const toggleCam = async () => {
    if (!session?.localTracks?.[1]) return;
    const next = !camOn;
    await session.localTracks[1].setEnabled(next);
    setCamOn(next);
  };
  const hangUp = () => {
    // Component cleanup releases this account-owned private session.
    onClose();
  };

  const localVideo = session?.localTracks?.[1] || null;
  const remoteUser = remoteUsers.find((u) => u.videoTrack);

  return (
    <div className="video-modal" data-testid="meeting-room-modal">
      <style>{MODAL_STYLES}</style>
      <div className="video-modal-shell wide">
        <header>
          <div>
            <div className="eyebrow light">PRIVATE MEETING</div>
            <h3>with {exhibitorName || "your buyer"}</h3>
          </div>
          <div className="video-modal-actions">
            <span className="conn-state" data-testid="meeting-conn-state">{connState}</span>
            <button className="video-close-btn" onClick={hangUp} aria-label="Close" data-testid="meeting-close">
              <X size={16}/>
            </button>
          </div>
        </header>
        <div className="video-modal-body">
          {error && <div className="video-error" data-testid="meeting-error">{error}</div>}
          {!error && connecting && <div className="video-loading">Joining the meeting…</div>}
          {!error && !connecting && (
            <div className="meeting-stage">
              <div className="meeting-remote">
                {remoteUser
                  ? <TrackVideo track={remoteUser.videoTrack}/>
                  : <div className="video-loading" data-testid="meeting-waiting">Waiting for the other participant…</div>}
              </div>
              <div className="meeting-self">
                <div className="eyebrow light" style={{ marginBottom: 6 }}>YOU</div>
                {camOn && localVideo
                  ? <TrackVideo track={localVideo}/>
                  : <div className="video-cam-off"><CameraOff size={20}/> Camera off</div>}
              </div>
            </div>
          )}
        </div>
        <footer className="meeting-controls">
          <button
            className={`meeting-ctrl ${micOn ? "" : "off"}`}
            onClick={toggleMic}
            disabled={!session}
            data-testid="meeting-mic-toggle"
            aria-pressed={micOn}
          >
            {micOn ? <Mic size={16}/> : <MicOff size={16}/>}
            {micOn ? "Mute" : "Unmute"}
          </button>
          <button
            className={`meeting-ctrl ${camOn ? "" : "off"}`}
            onClick={toggleCam}
            disabled={!session}
            data-testid="meeting-cam-toggle"
            aria-pressed={camOn}
          >
            {camOn ? <Camera size={16}/> : <CameraOff size={16}/>}
            {camOn ? "Stop camera" : "Start camera"}
          </button>
          <button className="meeting-ctrl leave" onClick={hangUp} data-testid="meeting-leave">
            <PhoneOff size={16}/> Leave meeting
          </button>
        </footer>
      </div>
    </div>
  );
}

// ---------- styles ----------
const BROADCAST_STYLES = `
.broadcast-panel{border:1px solid var(--line);padding:22px 24px;background:#fff;margin-bottom:24px}
.broadcast-panel-top{display:flex;justify-content:space-between;gap:24px;align-items:flex-start;flex-wrap:wrap}
.broadcast-title{font:500 22px/1.15 'Manrope',sans-serif;margin:8px 0 6px;letter-spacing:-.01em}
.broadcast-copy{font-size:12px;line-height:1.7;color:#777;max-width:520px;margin:0}
.broadcast-actions{display:flex;gap:8px}
.button.button-danger{background:#a83a2c;border-color:#a83a2c}
.broadcast-preview{position:relative;margin-top:20px;background:#0d0d0d;aspect-ratio:16/9;max-height:340px;overflow:hidden}
.broadcast-preview .video-surface{position:absolute;inset:0}
.broadcast-preview .live-tag{position:absolute;top:14px;left:14px;z-index:2}
.broadcast-error{margin-top:14px;font:10px 'DM Mono',monospace;color:#a83a2c;background:#fbecea;border:1px solid #e0b6ae;padding:10px 12px}
.live-tag{display:inline-flex;align-items:center;gap:6px;font:600 10px 'DM Mono',monospace;letter-spacing:.1em;color:#fff;background:#c4664f;padding:6px 9px;text-transform:uppercase}
.live-dot{display:inline-block;width:6px;height:6px;border-radius:50%;background:#fff;animation:pulse 1.5s ease-in-out infinite}
@keyframes pulse{0%,100%{opacity:1}50%{opacity:.35}}
.video-surface{width:100%;height:100%}
`;

const MODAL_STYLES = `
.video-modal{position:fixed;inset:0;z-index:5000;background:rgba(6,6,6,.86);display:grid;place-items:center;padding:24px}
.video-modal-shell{background:#0f0f0e;color:#f6f5ef;width:min(920px,100%);max-height:90vh;display:flex;flex-direction:column;border:1px solid #262624}
.video-modal-shell.wide{width:min(1080px,100%)}
.video-modal-shell header{padding:16px 22px;display:flex;justify-content:space-between;align-items:center;border-bottom:1px solid #262624;gap:16px}
.video-modal-shell header h3{margin:4px 0 0;font:500 16px/1.2 'Manrope',sans-serif;letter-spacing:.01em}
.video-modal-actions{display:flex;align-items:center;gap:12px}
.video-close-btn{background:transparent;border:1px solid #333;color:#f6f5ef;width:30px;height:30px;display:grid;place-items:center;cursor:pointer}
.video-close-btn:hover{background:#1c1c1a}
.video-modal-body{padding:22px;flex:1;min-height:0;display:flex;flex-direction:column}
.video-stage{background:#000;aspect-ratio:16/9;position:relative;overflow:hidden}
.video-stage .video-surface{position:absolute;inset:0}
.video-loading{padding:60px 20px;text-align:center;font:11px 'DM Mono',monospace;color:#8a8a82;letter-spacing:.05em}
.video-error{background:#3a1a15;border:1px solid #6a2f27;color:#f6c8bf;padding:14px 16px;font:11px 'DM Mono',monospace;letter-spacing:.03em;line-height:1.6}
.eyebrow.light{color:#a5a59e}
.meeting-stage{display:grid;grid-template-columns:1fr 260px;gap:14px;flex:1;min-height:0}
.meeting-remote{background:#000;position:relative;overflow:hidden;min-height:300px}
.meeting-remote .video-surface,.meeting-remote .video-loading{position:absolute;inset:0;display:grid;place-items:center}
.meeting-self{background:#111;padding:12px;display:flex;flex-direction:column;gap:8px}
.meeting-self .video-surface{flex:1;background:#000;min-height:160px}
.video-cam-off{flex:1;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:8px;color:#7a7a72;font:11px 'DM Mono',monospace;background:#0a0a09}
.conn-state{font:9px 'DM Mono',monospace;letter-spacing:.08em;text-transform:uppercase;color:#8a8a82}
.meeting-controls{padding:14px 22px;border-top:1px solid #262624;display:flex;gap:10px;justify-content:center;flex-wrap:wrap}
.meeting-ctrl{background:transparent;border:1px solid #333;color:#f6f5ef;padding:10px 16px;font:600 12px 'Manrope',sans-serif;letter-spacing:.02em;display:inline-flex;align-items:center;gap:8px;cursor:pointer}
.meeting-ctrl:hover:not(:disabled){background:#1c1c1a}
.meeting-ctrl:disabled{opacity:.45;cursor:not-allowed}
.meeting-ctrl.off{background:#3a1a15;border-color:#6a2f27;color:#f6c8bf}
.meeting-ctrl.leave{background:#a83a2c;border-color:#a83a2c;color:#fff}
.meeting-ctrl.leave:hover{background:#8f2e21}
@media(max-width:800px){
  .meeting-stage{grid-template-columns:1fr}
  .meeting-self{max-height:200px}
}
`;
